import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { banksApi, type BankMaster } from '../lib/api/banks';
import { employeesApi, type CreateEmployeeInput, type Employee, type HrCandidate } from '../lib/api/employees';
import { salaryConfigApi, type SalaryAllowance } from '../lib/api/salary-config';

interface Props {
  open: boolean;
  editingId?: string | null;
  onClose: () => void;
  onSaved: () => void;
}

function shortAmount(n: number | null): string {
  if (n == null) return '';
  if (n >= 1_000_000) return `${n / 1_000_000}tr`.replace('.0tr', 'tr');
  if (n >= 1_000) return `${Math.round(n / 1_000)}k`;
  return String(n);
}

const CONTRACT_LABEL: Record<string, string> = { CT: 'Chính thức', TV: 'Thử việc', DV: 'Freelance / Dịch vụ' };

function isoDate(d?: string | null): string {
  if (!d) return '';
  return d.slice(0, 10);
}

function formatDate(d?: string | null): string {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '—';
  return `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function fmtVND(n: number): string {
  return n.toLocaleString('vi-VN');
}

export function EmployeeFormModal({ open, editingId, onClose, onSaved }: Props) {
  const isEdit = Boolean(editingId);

  const [candidates, setCandidates] = useState<HrCandidate[]>([]);
  const [selectedCode, setSelectedCode] = useState('');
  const [editingEmp, setEditingEmp] = useState<Employee | null>(null);
  const [active, setActive] = useState(true);

  // Salary
  const [agreedSalary, setAgreedSalary] = useState<number>(0);
  const [salaryType, setSalaryType] = useState<'GROSS' | 'NET'>('GROSS');
  const [salaryPct, setSalaryPct] = useState(100);
  const [socialInsuranceBase, setSocialInsuranceBase] = useState<number>(0);
  // Allowances — load master từ /api/salary-config, chỉ hiển thị active
  const [allowanceDefs, setAllowanceDefs] = useState<SalaryAllowance[]>([]);
  const [allowanceOn, setAllowanceOn] = useState<Set<string>>(new Set());
  const [allowanceAmts, setAllowanceAmts] = useState<Record<string, number>>({});
  // Probation
  const [probationStart, setProbationStart] = useState('');
  const [probationEnd, setProbationEnd] = useState('');
  const [probationSalaryPct, setProbationSalaryPct] = useState(85);
  // Tax
  const [taxCode, setTaxCode] = useState('');
  const [dependentsCount, setDependentsCount] = useState(0);
  // Bank
  const [banks, setBanks] = useState<BankMaster[]>([]);
  const [bankName, setBankName] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [bankBranch, setBankBranch] = useState('');
  // Effective
  const [effectiveFrom, setEffectiveFrom] = useState(new Date().toISOString().slice(0, 10));
  const [effectiveNote, setEffectiveNote] = useState('');
  const [selectedSalaryHistoryId, setSelectedSalaryHistoryId] = useState<string | null>(null);

  const [loadingEdit, setLoadingEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const selectedCand = useMemo(
    () => candidates.find((c) => c.code === selectedCode) ?? null,
    [candidates, selectedCode],
  );

  useEffect(() => {
    if (!open) return;
    setErr(null); setSaving(false);
    setSelectedCode('');
    setActive(true);
    setAgreedSalary(0); setSalaryType('GROSS'); setSalaryPct(100); setSocialInsuranceBase(0);
    setAllowanceOn(new Set());
    setAllowanceAmts({});
    setProbationStart(''); setProbationEnd(''); setProbationSalaryPct(85);
    setTaxCode(''); setDependentsCount(0);
    setBankName(''); setBankAccount(''); setBankBranch('');
    setEffectiveFrom(new Date().toISOString().slice(0, 10)); setEffectiveNote('');
    setSelectedSalaryHistoryId(null);
    setEditingEmp(null);

    // Load banks master từ Cài đặt · Ngân hàng
    banksApi.list().then((res) => {
      const active = res.filter((b) => b.active);
      setBanks(active);
      if (!editingId && active.length > 0) setBankName(active[0].name);
    }).catch(() => setBanks([]));

    // Load allowance master từ Cài đặt · Chi phí lương
    salaryConfigApi.get().then((res) => {
      const active = res.allowances.filter((a) => a.active);
      setAllowanceDefs(active);
      // Add mode: mặc định bật các loại có defaultAmount, dùng default amount
      if (!editingId) {
        const on = new Set<string>();
        const amts: Record<string, number> = {};
        for (const a of active) {
          if (a.defaultAmount != null) { on.add(a.code); amts[a.code] = a.defaultAmount; }
        }
        setAllowanceOn(on); setAllowanceAmts(amts);
      }
    }).catch((e2) => setErr(e2 instanceof Error ? e2.message : String(e2)));

    if (editingId) {
      setLoadingEdit(true);
      employeesApi.findOne(editingId).then((e: Employee) => {
        setEditingEmp(e);
        setActive(e.active);
        setAgreedSalary(e.agreedSalary ?? 0);
        setSalaryType((e.salaryType as 'GROSS' | 'NET') ?? 'GROSS');
        setSalaryPct(e.salaryPct ?? 100);
        setSocialInsuranceBase(e.socialInsuranceBase ?? 0);
        setProbationStart(isoDate(e.probationStart));
        setProbationEnd(isoDate(e.probationEnd));
        setProbationSalaryPct(e.probationSalaryPct ?? 85);
        setTaxCode(e.taxCode ?? '');
        setDependentsCount(e.dependentsCount);
        setBankName(e.bankName ?? '');
        setBankAccount(e.bankAccount ?? '');
        setBankBranch(e.bankBranch ?? '');
        setEffectiveFrom(isoDate(e.effectiveFrom) || new Date().toISOString().slice(0, 10));
        setEffectiveNote(e.effectiveNote ?? '');
        // Hydrate allowance từ NV
        const on = new Set(e.allowances.map((a) => a.code));
        const amts: Record<string, number> = {};
        for (const a of e.allowances) amts[a.code] = a.amount;
        setAllowanceOn(on); setAllowanceAmts(amts);
      }).catch((e2) => setErr(e2 instanceof Error ? e2.message : String(e2)))
        .finally(() => setLoadingEdit(false));
    } else {
      employeesApi.hrDirectory().then((res) => setCandidates(res.items))
        .catch((e2) => setErr(e2 instanceof Error ? e2.message : String(e2)));
    }
  }, [open, editingId]);

  // Auto-fill probation dates khi chọn TV candidate
  useEffect(() => {
    if (!selectedCand) return;
    if (selectedCand.employmentType === 'TV' && !probationStart) {
      const start = selectedCand.hireDate || new Date().toISOString().slice(0, 10);
      const s = new Date(start);
      const end = new Date(s);
      end.setMonth(end.getMonth() + 2);
      setProbationStart(start);
      setProbationEnd(end.toISOString().slice(0, 10));
    }
  }, [selectedCand, probationStart]);

  if (!open) return null;

  // Header avatar + title
  const empInfo = isEdit
    ? editingEmp && {
        code: editingEmp.code, name: editingEmp.name,
        position: editingEmp.position, departmentName: editingEmp.departmentName,
        employmentType: editingEmp.employmentType, hireDate: editingEmp.hireDate,
        active: editingEmp.active, cccd: editingEmp.cccd,
        bhxhBookNumber: editingEmp.bhxhBookNumber, bhStartDate: editingEmp.bhStartDate,
      }
    : selectedCand && {
        code: selectedCand.code, name: selectedCand.name,
        position: selectedCand.position, departmentName: selectedCand.departmentName,
        employmentType: selectedCand.employmentType, hireDate: selectedCand.hireDate,
        active: true, cccd: selectedCand.cccd ?? null,
        bhxhBookNumber: selectedCand.bhxhBookNumber ?? null,
        bhStartDate: selectedCand.bhStartDate ?? null,
      };

  const contractType = empInfo?.employmentType ?? 'CT';

  // Tổng phụ cấp
  const totalAllowances = allowanceDefs.reduce(
    (sum, a) => (allowanceOn.has(a.code) ? sum + (allowanceAmts[a.code] || 0) : sum),
    0,
  );
  const totalIncome = agreedSalary + totalAllowances;

  const toggleAllowance = (code: string) => {
    setAllowanceOn((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!isEdit && !selectedCode) return setErr('Chọn NV từ danh sách HR trước.');
    if (contractType === 'TV' && (!probationStart || !probationEnd)) {
      return setErr('Thử việc: nhập ngày bắt đầu + kết thúc.');
    }
    if (!isEdit && agreedSalary < 1_000_000) {
      return setErr('Lương thoả thuận tối thiểu 1.000.000đ.');
    }
    setSaving(true);
    try {
      const allowancesPayload = allowanceDefs
        .filter((a) => allowanceOn.has(a.code))
        .map((a) => ({ code: a.code, amount: allowanceAmts[a.code] || 0 }));
      const payload: Partial<CreateEmployeeInput> = {
        probationStart: contractType === 'TV' ? probationStart : undefined,
        probationEnd: contractType === 'TV' ? probationEnd : undefined,
        probationSalaryPct: contractType === 'TV' ? probationSalaryPct : undefined,
        agreedSalary: agreedSalary || undefined,
        salaryType, salaryPct,
        socialInsuranceBase: socialInsuranceBase || undefined,
        taxCode: taxCode.trim() || undefined,
        dependentsCount,
        bankName, bankAccount: bankAccount.trim() || undefined,
        bankBranch: bankBranch.trim() || undefined,
        effectiveFrom, effectiveNote: effectiveNote.trim() || undefined,
        allowances: allowancesPayload,
      };
      if (isEdit && editingId) {
        await employeesApi.update(editingId, { ...payload, active } as Partial<CreateEmployeeInput> & { active?: boolean });
      } else if (selectedCode) {
        await employeesApi.create({ hrCandidateCode: selectedCode, ...payload } as CreateEmployeeInput);
      }
      onSaved();
    } catch (e2) { setErr(e2 instanceof Error ? e2.message : String(e2)); }
    finally { setSaving(false); }
  };

  const showBody = isEdit ? Boolean(editingEmp) : true;

  return (
    <div className="cvm-backdrop" onClick={onClose}>
      <div className="cvm-modal hr-modal-v2" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={handleSubmit}>
          {/* HEADER */}
          <div className="hr-modal-head">
            <div className="hr-modal-head-left">
              <div className={`hr-modal-avatar ${isEdit ? `type-${contractType}` : 'add'}`}>
                {isEdit ? initials(empInfo?.name ?? '') : '+'}
              </div>
              <div>
                <h3>{isEdit ? `Sửa lương · ${empInfo?.name ?? ''}` : 'Thêm NV vào cấu hình lương'}</h3>
                <p>
                  {isEdit
                    ? `${empInfo?.code} · ${empInfo?.position ?? '—'} · ${empInfo?.departmentName ?? '—'} · Vào làm ${formatDate(empInfo?.hireDate)}`
                    : 'Chọn NV từ HR để bắt đầu cấu hình lương + thuế + BH'}
                </p>
              </div>
            </div>
            <button type="button" className="hr-modal-close" onClick={onClose}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          </div>

          {/* BODY */}
          <div className="hr-modal-body">
            {loadingEdit ? <div className="pay-empty">Đang tải…</div> : showBody && (
              <div className="hr-modal-body-inner">
                {/* Section 1a: HR picker (add mode, always visible) */}
                {!isEdit && (
                  <div className="hr-sec-picker">
                    <div className="hr-sec-picker-head">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1d4ed8" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="8" r="4" /><path d="M20 21a8 8 0 1 0-16 0" /></svg>
                      <span>Chọn NV từ HR</span>
                    </div>
                    <label className="hr-sec-label">Nhân viên <span className="req">*</span></label>
                    <HrCandidateCombobox
                      candidates={candidates}
                      value={selectedCode}
                      onChange={setSelectedCode}
                    />
                    <div className="hr-sec-picker-note">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 8v4M12 16h.01" /><circle cx="12" cy="12" r="10" /></svg>
                      <span>Chỉ hiển thị NV đã có trong HR mà chưa được cấu hình lương. NV mới cần được HR add trước.</span>
                    </div>
                  </div>
                )}

                {/* Section 1b: HR info (edit mode) */}
                {isEdit && empInfo && (
                  <div className="hr-sec-info">
                    <div className="hr-sec-info-head">
                      <div className="hr-sec-info-title">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="11" x="3" y="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                        <span>Thông tin từ HR · Chỉ đọc</span>
                      </div>
                      <span className="hr-sec-info-sub">Sửa tại phần mềm HR</span>
                    </div>
                    <div className="hr-sec-info-grid">
                      <InfoField label="Chức vụ" value={empInfo.position} />
                      <InfoField label="Phòng ban" value={empInfo.departmentName} />
                      <InfoField label="Loại hợp đồng" value={CONTRACT_LABEL[empInfo.employmentType ?? 'CT'] ?? empInfo.employmentType} />
                      <InfoField label="Ngày vào làm" value={formatDate(empInfo.hireDate)} mono />
                      <div>
                        <div className="hr-sec-info-lbl">Trạng thái</div>
                        <div>
                          <span className={`hr-status-pill ${empInfo.active ? 'active' : 'inactive'}`}>
                            <span className="dot" />
                            {empInfo.active ? 'Đang làm việc' : 'Đã nghỉ'}
                          </span>
                        </div>
                      </div>
                      <InfoField label="CCCD" value={empInfo.cccd} mono />
                      <InfoField label="Số sổ BHXH" value={empInfo.bhxhBookNumber} mono />
                      <InfoField label="Ngày tham gia BH" value={formatDate(empInfo.bhStartDate)} mono />
                    </div>
                  </div>
                )}

                {/* Probation (only if TV) */}
                {contractType === 'TV' && (isEdit || selectedCand) && (
                  <div className="hr-sec-card">
                    <div className="hr-sec-card-head">
                      <span className="hr-sec-card-title purple">Trạng thái hợp đồng · Thử việc</span>
                    </div>
                    <div className="hr-sec-grid-3">
                      <Field label="Ngày bắt đầu TV" required>
                        <input type="date" value={probationStart} onChange={(e) => setProbationStart(e.target.value)} required />
                      </Field>
                      <Field label="Ngày lên chính thức" required>
                        <input type="date" value={probationEnd} onChange={(e) => setProbationEnd(e.target.value)} required />
                      </Field>
                      <Field label="% lương thử việc">
                        <input type="number" min={50} max={100} value={probationSalaryPct} onChange={(e) => setProbationSalaryPct(Math.max(50, Math.min(100, Number(e.target.value) || 85)))} />
                      </Field>
                    </div>
                  </div>
                )}

                {/* Salary + allowances */}
                {(isEdit || selectedCand) && (
                  <div className="hr-sec-card">
                    <div className="hr-sec-card-head">
                      <span className="hr-sec-card-title">Lương & phụ cấp cố định</span>
                      <span className="hr-sec-card-sub">Áp dụng cho bảng lương tiếp theo</span>
                    </div>
                    <div className="hr-sec-grid-3">
                      <Field label="Lương thoả thuận / tháng">
                        <div className="hr-input-with-suffix">
                          <input
                            className="right"
                            inputMode="numeric" placeholder="10.000.000"
                            value={agreedSalary ? fmtVND(agreedSalary) : ''}
                            onChange={(e) => setAgreedSalary(Number(e.target.value.replace(/\D/g, '')) || 0)}
                            required
                          />
                          <span>đ</span>
                        </div>
                      </Field>
                      <Field label="Loại lương">
                        <select value={salaryType} onChange={(e) => setSalaryType(e.target.value as 'GROSS' | 'NET')}>
                          <option value="GROSS">GROSS</option>
                          <option value="NET">NET</option>
                        </select>
                      </Field>
                      <Field label="Mức hưởng">
                        <div className="hr-input-with-suffix">
                          <input className="right" type="number" min={0} max={100} value={salaryPct} onChange={(e) => setSalaryPct(Math.max(0, Math.min(100, Number(e.target.value) || 100)))} />
                          <span>%</span>
                        </div>
                      </Field>
                    </div>

                    <div className="hr-sec-sub-head">
                      <span>Phụ cấp cố định / tháng</span>
                      <span className="hr-sec-card-sub">
                        Chỉ hiển thị loại đang bật ở{' '}
                        <Link to="/settings?tab=salary" target="_blank" className="hr-sec-card-link">
                          Cài đặt · Chi phí lương
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></svg>
                        </Link>
                      </span>
                    </div>
                    <div className="hr-allowance-grid">
                      {allowanceDefs.length === 0 ? (
                        <div className="hr-allowance-empty">
                          Chưa có phụ cấp nào được bật. Thêm/bật tại{' '}
                          <Link to="/settings?tab=salary" target="_blank">Cài đặt · Chi phí lương</Link>.
                        </div>
                      ) : allowanceDefs.map((a) => {
                        const on = allowanceOn.has(a.code);
                        const amt = allowanceAmts[a.code] ?? a.defaultAmount ?? 0;
                        const override = on && a.defaultAmount != null && amt !== a.defaultAmount;
                        const hint = a.defaultAmount != null ? `mặc định ${shortAmount(a.defaultAmount)}` : a.suggestedLabel;
                        return (
                          <label key={a.code} className={`hr-allowance-row ${override ? 'override' : ''}`}>
                            <input type="checkbox" checked={on} onChange={() => toggleAllowance(a.code)} />
                            <span className="hr-allowance-name">
                              {a.name} <span className="hr-allowance-code">({a.code})</span>
                            </span>
                            {override && a.defaultAmount != null ? (
                              <button
                                type="button"
                                className="hr-allowance-reset"
                                title={`Đưa về mức mặc định ${shortAmount(a.defaultAmount)}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setAllowanceAmts((prev) => ({ ...prev, [a.code]: a.defaultAmount as number }));
                                }}
                              >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /></svg>
                                override · reset {shortAmount(a.defaultAmount)}
                              </button>
                            ) : (
                              <span className="hr-allowance-hint">{hint}</span>
                            )}
                            <input
                              className={`hr-allowance-input ${override ? 'strong' : ''}`}
                              disabled={!on}
                              value={fmtVND(amt)}
                              onChange={(e) => setAllowanceAmts((prev) => ({ ...prev, [a.code]: Number(e.target.value.replace(/\D/g, '')) || 0 }))}
                            />
                          </label>
                        );
                      })}
                    </div>

                    <div className="hr-sec-total">
                      <b>Tổng thu nhập / tháng:</b> {fmtVND(agreedSalary)}đ + {fmtVND(totalAllowances)}đ phụ cấp = <b>{fmtVND(totalIncome)}đ</b>
                    </div>
                  </div>
                )}

                {/* Tax */}
                {(isEdit || selectedCand) && (
                  <div className="hr-sec-card">
                    <div className="hr-sec-card-head">
                      <span className="hr-sec-card-title">Thuế TNCN</span>
                    </div>
                    <div className="hr-sec-grid-2">
                      <Field label="MST cá nhân">
                        <input value={taxCode} onChange={(e) => setTaxCode(e.target.value)} placeholder="8xxxxxxxxx" />
                      </Field>
                      <Field label="Số người phụ thuộc (NPT)">
                        <input className="right" type="number" min={0} max={20} value={dependentsCount} onChange={(e) => setDependentsCount(Math.max(0, Number(e.target.value) || 0))} />
                      </Field>
                    </div>
                    <div className="hr-sec-note">Mỗi NPT được giảm trừ 4.400.000đ/tháng khi tính thuế TNCN</div>
                  </div>
                )}

                {/* Bank */}
                {(isEdit || selectedCand) && (
                  <div className="hr-sec-card">
                    <div className="hr-sec-card-head">
                      <span className="hr-sec-card-title">Tài khoản chi lương</span>
                      <span className="hr-sec-card-sub">
                        Danh sách NH quản lý tại{' '}
                        <Link to="/settings?tab=banks" target="_blank" className="hr-sec-card-link">
                          Cài đặt · Ngân hàng
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></svg>
                        </Link>
                      </span>
                    </div>
                    <div className="hr-sec-grid-bank">
                      <Field label="Ngân hàng">
                        <select value={bankName} onChange={(e) => setBankName(e.target.value)}>
                          {banks.length === 0 && <option value="">— Chưa có NH nào —</option>}
                          {/* Nếu bankName cũ không có trong list (VD legacy), vẫn hiện để user thấy giá trị hiện tại */}
                          {bankName && !banks.some((b) => b.name === bankName) && (
                            <option value={bankName}>{bankName} (không còn trong danh sách)</option>
                          )}
                          {banks.map((b) => (
                            <option key={b.id} value={b.name}>
                              {b.name}{b.shortName ? ` (${b.shortName})` : ''}
                            </option>
                          ))}
                        </select>
                      </Field>
                      <Field label="Số tài khoản">
                        <input value={bankAccount} onChange={(e) => setBankAccount(e.target.value)} placeholder="0071234567890" />
                      </Field>
                      <Field label="Chi nhánh">
                        <input value={bankBranch} onChange={(e) => setBankBranch(e.target.value)} placeholder="TPHCM" />
                      </Field>
                    </div>
                  </div>
                )}

                {/* Effective (edit mode only) */}
                {isEdit && (
                  <div className="hr-sec-card">
                    <div className="hr-sec-grid-eff">
                      <Field label="Áp dụng từ" tight>
                        <input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
                      </Field>
                      <Field label="Ghi chú thay đổi" tight>
                        <input value={effectiveNote} onChange={(e) => setEffectiveNote(e.target.value)} placeholder="VD: Tăng lương theo review Q2/2026" />
                      </Field>
                    </div>
                  </div>
                )}

                {isEdit && (
                  <div className="hr-sec-card hr-salary-history-card">
                    <div className="hr-sec-card-head">
                      <span className="hr-sec-card-title">Lịch sử điều chỉnh lương</span>
                      <span className="hr-sec-card-sub">Payroll lấy mức có hiệu lực tại kỳ lương</span>
                    </div>
                    {editingEmp?.salaryHistory.length ? (
                      <div className="hr-salary-history-list">
                        {editingEmp.salaryHistory.map((item, index) => {
                          const totalIncomeAtEffectiveDate = item.agreedSalary + item.allowanceTotal;
                          const isCurrentSalary = index === 0;
                          const selected = selectedSalaryHistoryId === item.id;
                          return (
                          <div className={`hr-salary-history-entry ${selected ? 'is-selected' : ''} ${isCurrentSalary ? 'is-current' : ''}`} key={item.id}>
                          {isCurrentSalary ? (
                            <div className="hr-salary-history-row">
                            <div className="hr-salary-history-date">Từ {formatDate(item.effectiveFrom)}</div>
                            <div className="hr-salary-history-main">
                              <b>{fmtVND(totalIncomeAtEffectiveDate)}đ</b>
                              <span>Tổng thu nhập hiện tại</span>
                            </div>
                            <div className="hr-salary-history-note">{item.note || '—'}{item.changedBy ? ` · ${item.changedBy}` : ''}</div>
                            </div>
                          ) : (
                            <button type="button" className="hr-salary-history-row" onClick={() => setSelectedSalaryHistoryId(selected ? null : item.id)} aria-expanded={selected}>
                              <div className="hr-salary-history-date">Từ {formatDate(item.effectiveFrom)}</div>
                              <div className="hr-salary-history-main">
                                <b>{fmtVND(totalIncomeAtEffectiveDate)}đ</b>
                                <span>Tổng thu nhập · bấm để xem chi tiết</span>
                              </div>
                              <div className="hr-salary-history-note">{item.note || '—'}{item.changedBy ? ` · ${item.changedBy}` : ''}<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="m6 9 6 6 6-6" /></svg></div>
                            </button>
                          )}
                          {!isCurrentSalary && selected && <div className="hr-salary-history-detail">
                            <div><span>Lương thỏa thuận</span><b>{fmtVND(item.agreedSalary)}đ</b></div>
                            <div><span>Phụ cấp cố định</span><b>{fmtVND(item.allowanceTotal)}đ</b></div>
                            <div><span>Lương đóng BH</span><b>{item.socialInsuranceBase != null ? `${fmtVND(item.socialInsuranceBase)}đ` : '—'}</b></div>
                            <div><span>Loại lương · Mức hưởng</span><b>{item.salaryType ?? 'GROSS'} · {item.salaryPct ?? 100}%</b></div>
                          </div>}
                          </div>
                        );
                        })}
                      </div>
                    ) : (
                      <div className="hr-sec-note">Chưa có lịch sử. Khi lưu thay đổi, hệ thống sẽ tạo một mốc lương theo ngày áp dụng.</div>
                    )}
                  </div>
                )}

                {err && <div className="cvm-error">{err}</div>}
              </div>
            )}
          </div>

          {/* FOOTER */}
          <div className="hr-modal-foot">
            <div className="hr-modal-foot-note">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10" /><path d="M12 8v4M12 16h.01" /></svg>
              Áp dụng cho bảng lương từ ngày hiệu lực · Có audit log
            </div>
            <div className="hr-modal-foot-actions">
              {isEdit && (
                <label className="hr-modal-active">
                  <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                  <span>Đang làm việc</span>
                </label>
              )}
              <button type="button" className="hr-btn-cancel" onClick={onClose} disabled={saving}>Hủy</button>
              <button type="submit" className="hr-btn-save" disabled={saving || loadingEdit || (!isEdit && !selectedCode)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6 9 17l-5-5" /></svg>
                {saving ? 'Đang lưu…' : 'Lưu cấu hình lương'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, tight, children }: { label: string; required?: boolean; tight?: boolean; children: React.ReactNode }) {
  return (
    <div className={`hr-field ${tight ? 'tight' : ''}`}>
      <label>{label}{required && <span className="req"> *</span>}</label>
      {children}
    </div>
  );
}

function InfoField({ label, value, mono }: { label: string; value?: string | null; mono?: boolean }) {
  return (
    <div>
      <div className="hr-sec-info-lbl">{label}</div>
      <div className={`hr-sec-info-val ${mono ? 'mono' : ''}`}>{value || '—'}</div>
    </div>
  );
}

function HrCandidateCombobox({
  candidates,
  value,
  onChange,
}: {
  candidates: HrCandidate[];
  value: string;
  onChange: (code: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(
    () => candidates.find((c) => c.code === value) ?? null,
    [candidates, value],
  );

  const filtered = useMemo(() => {
    const s = search.toLowerCase().trim();
    if (!s) return candidates;
    return candidates.filter(
      (c) =>
        c.code.toLowerCase().includes(s) ||
        c.name.toLowerCase().includes(s) ||
        c.position.toLowerCase().includes(s) ||
        c.departmentName.toLowerCase().includes(s) ||
        (c.email || '').toLowerCase().includes(s),
    );
  }, [candidates, search]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false); setSearch('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => { setHighlight(0); }, [search, open]);

  const pick = (code: string) => {
    onChange(code); setOpen(false); setSearch('');
  };

  const displayText = selected
    ? `${selected.code} · ${selected.name} · ${selected.position} · ${selected.departmentName}`
    : '';

  return (
    <div className={`hr-combo ${open ? 'open' : ''}`} ref={wrapRef}>
      <button
        type="button"
        className="hr-combo-trigger"
        onClick={() => setOpen((v) => !v)}
      >
        <span className={selected ? '' : 'hr-combo-placeholder'}>
          {displayText || '— Chọn NV cần cấu hình lương —'}
        </span>
        <svg className={`hr-combo-chev ${open ? 'up' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="m6 9 6 6 6-6" /></svg>
      </button>
      {open && (
        <div className="hr-combo-panel">
          <div className="hr-combo-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm theo mã, tên, chức vụ, phòng ban…"
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(filtered.length - 1, h + 1)); }
                else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(0, h - 1)); }
                else if (e.key === 'Enter') { e.preventDefault(); if (filtered[highlight]) pick(filtered[highlight].code); }
                else if (e.key === 'Escape') { setOpen(false); setSearch(''); }
              }}
            />
          </div>
          <div className="hr-combo-list">
            {filtered.length === 0 ? (
              <div className="hr-combo-empty">Không tìm thấy NV khớp "{search}"</div>
            ) : filtered.map((c, i) => (
              <button
                key={c.hrEmployeeId}
                type="button"
                className={`hr-combo-item ${i === highlight ? 'active' : ''} ${c.code === value ? 'selected' : ''}`}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => pick(c.code)}
              >
                <div className="hr-combo-item-line1">
                  <span className="hr-combo-item-code">{c.code}</span>
                  <span className="hr-combo-item-name">{c.name}</span>
                </div>
                <div className="hr-combo-item-line2">{c.position} · {c.departmentName}</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
